import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChildProfile } from '../models/childProfile';
import './ChildSelect.scss';

interface Props {
  childProfiles: ChildProfile[];
  value: number | null;
  onChange: (id: number | null) => void;
  /** Show a "No child" option that clears the selection. Off by default
   *  for forms that require a child (e.g. unscheduled-session add). */
  allowEmpty?: boolean;
  emptyLabel?: string;
  /** Placeholder shown on the trigger when nothing is selected. */
  placeholder?: string;
  /** Small caption above the chosen name. */
  label?: string;
  autoFocus?: boolean;
  className?: string;
}

// Reusable child-picker dropdown — same colour-coded avatar trigger and
// listbox the Weekly Schedule uses for its session-detail panel, so the
// "Add unscheduled session" modal on the dashboard reads as the same
// control. Outside-click + Escape dismiss are baked in.
const ChildSelect: React.FC<Props> = ({
  childProfiles,
  value,
  onChange,
  allowEmpty = false,
  emptyLabel = 'No child',
  placeholder = 'Choose a child',
  label = 'Child',
  autoFocus,
  className,
}) => {
  const [open, setOpen] = useState(false);
  // Coordinates for the portal-rendered dropdown. Captured from the
  // trigger's bounding rect on open and refreshed on scroll/resize so
  // the dropdown follows the trigger if the layout shifts.
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const computeCoords = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 6, left: r.left, width: r.width });
  };

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    computeCoords();
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inWrap = wrapRef.current?.contains(t);
      // Dropdown lives in a portal so the trigger's wrap doesn't contain
      // it — check it separately.
      const inDropdown = dropdownRef.current?.contains(t);
      if (!inWrap && !inDropdown) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => computeCoords();
    const onResize = () => computeCoords();
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    // Listen on capture phase so we catch scrolls inside scrollable
    // ancestors (e.g. the modal body) without coupling to a specific one.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useEffect(() => {
    if (autoFocus) triggerRef.current?.focus();
  }, [autoFocus]);

  const activeChild = value != null
    ? childProfiles.find(c => c.id === value) ?? null
    : null;
  const childColor = activeChild?.colour_code ?? '#9aa89a';
  const childInitials = activeChild?.initials ?? '?';

  const handleSelect = (id: number | null) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className={`child-select__wrap${className ? ` ${className}` : ''}`}
      style={{ ['--child-color' as string]: childColor } as React.CSSProperties}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`child-select__trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="child-select__circle">{childInitials}</span>
        <div className="child-select__info">
          <span className="child-select__label">{label}</span>
          <span
            className={`child-select__name${activeChild ? '' : ' child-select__name--empty'}`}
          >
            {activeChild
              ? `${activeChild.first_name} ${activeChild.last_name}`
              : placeholder}
          </span>
        </div>
        <i className={`fas fa-chevron-down child-select__chevron${open ? ' is-open' : ''}`} />
      </button>

      {open && coords && createPortal(
        <div
          ref={dropdownRef}
          className="child-select__dropdown"
          role="listbox"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            // Override SCSS default that uses right: 0 — fixed-positioned
            // dropdowns drive their own width via `coords.width`.
            right: 'auto',
          }}
        >
          {allowEmpty && (
            <>
              <button
                type="button"
                role="option"
                aria-selected={value == null}
                className={`child-select__option${value == null ? ' is-selected' : ''}`}
                onClick={() => handleSelect(null)}
              >
                <span className="child-select__option-circle child-select__option-circle--empty">
                  <i className="fas fa-minus" />
                </span>
                <span className="child-select__option-name child-select__option-name--muted">
                  {emptyLabel}
                </span>
                {value == null && (
                  <i className="fas fa-check child-select__option-check" />
                )}
              </button>
              {childProfiles.length > 0 && <div className="child-select__option-sep" />}
            </>
          )}

          {childProfiles.map(c => {
            const selected = c.id === value;
            return (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={selected}
                className={`child-select__option${selected ? ' is-selected' : ''}`}
                onClick={() => handleSelect(c.id)}
                style={{ ['--option-color' as string]: c.colour_code || '#9aa89a' } as React.CSSProperties}
              >
                <span
                  className="child-select__option-circle"
                  style={{ background: c.colour_code || '#9aa89a' }}
                >
                  {c.initials}
                </span>
                <span className="child-select__option-name">
                  {c.first_name} {c.last_name}
                </span>
                {selected && (
                  <i className="fas fa-check child-select__option-check" />
                )}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ChildSelect;
